import { Box, Caption, Card, Divider, Heading, StyledIcon } from "@bcc-ui/ui-components";
import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";

import { DesktopIcon } from "./icons/DesktopIcon";
import { LaptopIcon } from "./icons/LaptopIcon";
import { MobileIcon } from "./icons/MobileIcon";
import { MonitorIcon } from "./icons/MonitorIcon";
import { OthersIcon } from "./icons/OthersIcon";
import { TabletIcon } from "./icons/TabletIcon";
import { JobsStatusBadge } from "./job-status-badge/JobsStatusBadge";
import { JobStatusBadge } from "./job-status-badge/JobStatusBadge";
import style from "./total-imported-assets-card.scss";
import DataBearingIcon from "components/icons/DataBearing";
import { AssetImportJob } from "services/asset-management/AssetImportJobService";
import { AssetDeviceType, AssetOverview } from "services/asset-management/AssetService";
import { resolveColor } from "utils/commonFunctions";

interface BadgeProps {
    variant: "Neutral" | "Info";
    children: React.ReactNode;
    dataType: string;
}

const Badge = (props: BadgeProps) => {
    const color =
        props.variant === "Info"
            ? "var(--app-dataBearing-badge-backgroundColor)"
            : "var(--app-nonDataBearing-badge-backgroundColor)";

    return (
        <Box
            verticalAlignment={"Center"}
            horizontalAlignment={"Center"}
            gap={"Xxxs"}
            className={style.badgeBase}
            style={{ backgroundColor: color }}
            datatype={props.dataType}
        >
            {props.children}
        </Box>
    );
};

interface Props {
    data: AssetOverview;
    groupJobsData: AssetImportJob[];
}

export const TotalImportedAssetsCard = (props: Props) => {
    const { t } = useTranslation();

    const getAssetDetails = (assetType: AssetDeviceType) => {
        switch (assetType) {
            case AssetDeviceType.MOBILE:
                return {
                    color: "--app-chart-chartFirstSet",
                    label: t("Common.deviceType.mobile"),
                    caption: t("AssetManagement.overview.totalAssetsCard.helptext.smartphones"),
                    icon: <MobileIcon />,
                    iconBackgroundColor: "--app-mobile-icon-backgrounColor",
                };
            case AssetDeviceType.LAPTOP:
                return {
                    color: "--app-chart-chartSecondSet",
                    label: t("Common.deviceType.laptop"),
                    caption: t("AssetManagement.overview.totalAssetsCard.helptext.laptops"),
                    icon: <LaptopIcon />,
                    iconBackgroundColor: "--app-laptop-icon-backgrounColor",
                };
            case AssetDeviceType.TABLET:
                return {
                    color: "--app-chart-chartThirdSet",
                    label: t("Common.deviceType.tablet"),
                    caption: t("AssetManagement.overview.totalAssetsCard.helptext.tablets"),
                    icon: <TabletIcon />,
                    iconBackgroundColor: "--app-tablet-icon-backgrounColor",
                };
            case AssetDeviceType.DESKTOP:
                return {
                    color: "--app-chart-chartSeventhSet",
                    label: t("Common.deviceType.desktop"),
                    caption: t("AssetManagement.overview.totalAssetsCard.helptext.desktops"),
                    icon: <DesktopIcon />,
                    iconBackgroundColor: "--app-desktop-icon-backgrounColor",
                };
            case AssetDeviceType.MONITOR:
                return {
                    color: "--app-chart-chartEighthSet",
                    label: t("Common.deviceType.monitor"),
                    caption: t("AssetManagement.overview.totalAssetsCard.helptext.monitors"),
                    icon: <MonitorIcon />,
                    iconBackgroundColor: "--app-monitor-icon-backgrounColor",
                };
            case AssetDeviceType.OTHERS:
                return {
                    color: "--app-chart-chartNinthSet",
                    label: t("Common.deviceType.others"),
                    caption: t("AssetManagement.overview.totalAssetsCard.helptext.others"),
                    icon: <OthersIcon />,
                    iconBackgroundColor: "--app-others-icon-backgrounColor",
                };
            default:
                return;
        }
    };

    const calculatePercentage = (count: number) => {
        if (count === 0) {
            return 0;
        }

        return Math.round((count / props.data.totalAssets) * 1_000) / 10;
    };

    const orderedAssets = [
        ...props.data.breakdown.filter((asset) => asset.count > 0),
        ...props.data.breakdown.filter((asset) => asset.count === 0),
    ];

    return (
        <Card padding="None" dataType="TOTAL_IMPORTED_ASSETS" className={style.card}>
            <Box columnLayout padding={"Md"}>
                <Box flexWrap={"Wrap"} justifyContent={"SpaceBetween"} gap={"Xxs"}>
                    <Box flexWrap={"Wrap"} gap={"Sm"} className={style.header}>
                        <Box gap={"Xxxs"} className={style.header}>
                            <Heading variant={"h1"} className={style.heading}>
                                {props.data.totalAssets}
                            </Heading>

                            {props.data.totalAssets === 1
                                ? t("AssetManagement.asset").toLowerCase()
                                : t("AssetManagement.assets").toLowerCase()}
                        </Box>
                    </Box>

                    <Box flexWrap={"Wrap"} verticalAlignment="Center" className={style.badgeContainer}>
                        <Box gap={"Xxs"} verticalAlignment="Center">
                            <Badge variant="Info" dataType="DATA_BEARING">
                                <DataBearingIcon color={resolveColor("--app-dataBearing-badge-iconColor")} size={14} />
                                <Caption>{t("AssetManagement.dataBearingFilter.dataBearing")}</Caption>
                            </Badge>
                            <strong>{props.data.trackedDataBearingAssets}</strong>
                        </Box>
                        <Box gap={"Xxs"} verticalAlignment="Center">
                            <Badge variant="Neutral" dataType="NON_DATA_BEARING">
                                <Caption> {t("AssetManagement.dataBearingFilter.nonDataBearing")}</Caption>
                            </Badge>
                            <strong>{props.data.nonDataBearingAssets}</strong>
                        </Box>
                        <Divider direction="Vertical" className={style.divider} />
                        {props.groupJobsData.length === 1 && <JobStatusBadge job={props.groupJobsData[0]} />}

                        {props.groupJobsData.length > 1 && <JobsStatusBadge jobs={props.groupJobsData} />}
                    </Box>
                </Box>
            </Box>

            <Divider />

            <Box columnLayout paddingLeft="Sm" paddingRight="Sm" paddingTop="Sm" paddingBottom={"Md"} gap={"Sm"}>
                {orderedAssets.map((asset) => {
                    const assetDetails = getAssetDetails(asset.deviceType);

                    if (!assetDetails) {
                        return;
                    }

                    const hasAssets = asset.count > 0;
                    const percentage = asset.count ? calculatePercentage(asset.count) : 0;

                    return (
                        <Box
                            key={asset.deviceType}
                            gap={"Sm"}
                            className={!hasAssets ? style.disabled : undefined}
                            datatype={asset.deviceType}
                        >
                            <StyledIcon
                                size={58}
                                icon={assetDetails.icon}
                                backgroundColor={assetDetails.iconBackgroundColor}
                                variant="Square"
                            />

                            <Box
                                columnLayout
                                justifyContent={"SpaceBetween"}
                                className={classNames(style.chart, !hasAssets && style.justifyCenter)}
                            >
                                <Box justifyContent="SpaceBetween" gap={"Sm"} className={style.alignCenter}>
                                    <Box gap={"Xxs"}>
                                        <Heading variant="SUBTITLE_2">{assetDetails.label}</Heading>
                                        {hasAssets && asset.hasDataBearing && (
                                            <Badge variant="Info" dataType="DATA_BEARING">
                                                <DataBearingIcon
                                                    size={12}
                                                    color={resolveColor("--app-dataBearing-badge-iconColor")}
                                                />
                                                <Caption>{t("AssetManagement.dataBearingFilter.dataBearing")}</Caption>
                                            </Badge>
                                        )}
                                        {hasAssets && asset.hasNonDataBearing && (
                                            <Badge variant="Neutral" dataType="NON_DATA_BEARING">
                                                <Caption>
                                                    {t("AssetManagement.dataBearingFilter.nonDataBearing")}
                                                </Caption>
                                            </Badge>
                                        )}
                                    </Box>
                                    <Heading variant="SUBTITLE_2">{asset.count}</Heading>
                                </Box>

                                {hasAssets && (
                                    <Box justifyContent="SpaceBetween">
                                        <Caption greyTextStyle>{assetDetails.caption}</Caption>
                                        <Caption greyTextStyle>
                                            {t("AssetManagement.overview.riskLevelSummaryCards.ofFleet", {
                                                percentage: percentage,
                                            })}
                                        </Caption>
                                    </Box>
                                )}

                                {hasAssets && (
                                    <Box>
                                        <div
                                            style={{
                                                backgroundColor: "var(--theme-background-layer)",
                                                width: "100%",
                                                height: "8px",
                                                borderRadius: "4px",
                                            }}
                                        >
                                            <div
                                                className={style.barFill}
                                                style={{
                                                    backgroundColor: `var(${assetDetails.color})`,
                                                    width: `${percentage}%`,
                                                }}
                                            />
                                        </div>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Card>
    );
};
